import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { AlertCircle } from "lucide-react";
import { tableService } from "../../../services/tableService";
import { handleError } from "../../../utils/errorHandler";
import { createToast } from "../../ui/toast";
import TableHeader from "./TableHeader";
import TableList from "./TableList";
import ConfirmationDialog from "./ConfirmationDialog";
import LoadingSpinner from "../../common/LoadingSpinner";

const ShowTables = () => {
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState(new Set());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // En ShowTables.jsx, al inicio del componente
  const [dialogs, setDialogs] = useState({
    delete: { isOpen: false, tableId: null },
    join: { isOpen: false },
    separate: { isOpen: false, tableId: null },
  });

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tableService.listTables();
      if (response.success) {
        setTables(response.data || []);
      } else {
        setError(response.message);
        createToast(response.message, "error");
      }
    } catch (err) {
      const errorMessage = err.message || "Error al cargar las mesas";
      setError(errorMessage);
      createToast(errorMessage, "error");
      setTables([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (tableId, checked) => {
    const table = tables.find((t) => t.mesaId === tableId);

    if (!table) return;

    if (checked) {
      if (table.joinTable !== "SI") {
        createToast(
          "Solo se pueden seleccionar mesas que permitan unión",
          "error"
        );
        return;
      }

      if (table.tableStatus !== "ACTIVA" && table.tableStatus !== "CREADA") {
        createToast(
          "Solo se pueden seleccionar mesas activas o recién creadas",
          "error"
        );
        return;
      }
    }

    const newSelectedTables = new Set(selectedTables);
    if (checked) {
      newSelectedTables.add(tableId);
    } else {
      newSelectedTables.delete(tableId);
    }

    setSelectedTables(newSelectedTables);
    setError("");
  };

  const handleJoinTables = () => {
    if (!Array.isArray(tables) || tables.length === 0) {
      createToast("No hay mesas disponibles", "error");
      return;
    }

    if (selectedTables.size < 2) {
      setError("Debe seleccionar al menos 2 mesas para unir");
      createToast("Debe seleccionar al menos 2 mesas para unir", "error");
      return;
    }

    const allTablesExist = Array.from(selectedTables).every((tableId) =>
      tables.find((t) => t.mesaId === tableId)
    );

    if (!allTablesExist) {
      setError("Una o más mesas seleccionadas no existen");
      createToast("Una o más mesas seleccionadas no existen", "error");
      return;
    }

    const allCanJoin = Array.from(selectedTables).every((tableId) => {
      const table = tables.find((t) => t.mesaId === tableId);
      return table && table.joinTable === "SI";
    });

    if (!allCanJoin) {
      setError("Solo se pueden unir mesas que permitan unión");
      createToast("Solo se pueden unir mesas que permitan unión", "error");
      return;
    }

    const allValidStatus = Array.from(selectedTables).every((tableId) => {
      const table = tables.find((t) => t.mesaId === tableId);
      return (
        table &&
        (table.tableStatus === "ACTIVA" || table.tableStatus === "CREADA")
      );
    });

    if (!allValidStatus) {
      setError("Solo se pueden unir mesas activas o recién creadas");
      createToast(
        "Solo se pueden unir mesas activas o recién creadas",
        "error"
      );
      return;
    }

    setError("");
    setDialogs((prev) => ({ ...prev, join: { isOpen: true } }));
  };

  const handleDelete = (tableId) => {
    const table = tables.find((t) => t.mesaId === tableId);
    if (table.tableStatus !== "ACTIVA" && table.tableStatus !== "CREADA") {
      createToast(
        "No se puede eliminar una mesa que no está activa o recién creada",
        "error"
      );
      return;
    }
    setDialogs((prev) => ({ ...prev, delete: { isOpen: true, tableId } }));
  };

  const handleSeparateConfirm = async () => {
    console.log("Confirmando separación de mesas");
    const { tableId } = dialogs.separate;
    console.log("TableId a separar:", tableId);

    if (tableId) {
      try {
        const selectedTable = tables.find((t) => t.mesaId === tableId);
        console.log("Mesa seleccionada:", selectedTable);

        const listJoins = {
          tableId: tableId,
          joinTablesId: selectedTable.joinTablesId,
        };
        console.log("Datos a enviar:", listJoins);

        const response = await tableService.separateTables(listJoins);
        console.log("Respuesta del servidor:", response);

        if (response.success) {
          createToast("Mesas separadas exitosamente", "success");
          await loadTables();
        } else {
          createToast(response.message, "error");
        }
      } catch (err) {
        console.error("Error al separar mesas:", err);
        handleError(err);
        createToast(err.message, "error");
      }
    }
    setDialogs((prev) => ({
      ...prev,
      separate: { isOpen: false, tableId: null },
    }));
  };

  // const handleSeparate = (tableId) => {
  //   const table = tables.find((t) => t.mesaId === tableId);
  //   if (table.tableStatus !== "ACTIVA" && table.tableStatus !== "CREADA") {
  //     createToast(
  //       "No se puede separar una mesa que no está activa o recién creada",
  //       "error"
  //     );
  //     return;
  //   }
  //   setDialogs((prev) => ({ ...prev, separate: { isOpen: true, tableId } }));
  // };

  const handleDeleteConfirm = async () => {
    const { tableId } = dialogs.delete;
    if (tableId) {
      try {
        const response = await tableService.deleteTable(tableId);
        if (response.success) {
          createToast("Mesa eliminada exitosamente", "success");
          await loadTables();
        } else {
          createToast(response.message, "error");
        }
      } catch (err) {
        handleError(err);
        createToast(err.message, "error");
      }
    }
    setDialogs((prev) => ({
      ...prev,
      delete: { isOpen: false, tableId: null },
    }));
  };

  const handleJoinTablesConfirm = async () => {
    try {
      // Solo enviamos el array de IDs como datos
      const mesasIds = Array.from(selectedTables);
      console.log("IDs a enviar:", mesasIds); // Debug

      if (mesasIds.length < 2) {
        createToast("Debe seleccionar al menos 2 mesas para unir", "error");
        return;
      }

      const response = await tableService.joinTables(mesasIds);

      if (response.success) {
        createToast("Mesas unidas exitosamente", "success");
        await loadTables();
        setSelectedTables(new Set());
      } else {
        createToast(response.message, "error");
      }
    } catch (err) {
      handleError(err);
      createToast(err.message, "error");
    } finally {
      setDialogs((prev) => ({ ...prev, join: { isOpen: false } }));
    }
  };

  const handleSeparate = (tableId) => {
    console.log("Iniciando separación de mesa:", tableId);
    const table = tables.find((t) => t.mesaId === tableId);

    console.log("Mesa encontrada:", table);

    if (!table.isJoined) {
      console.log("Mesa no está unida");
      createToast("Esta mesa no está unida con otras mesas", "error");
      return;
    }

    console.log("Abriendo diálogo de separación");
    setDialogs((prev) => {
      const newDialogs = {
        ...prev,
        separate: { isOpen: true, tableId },
      };
      console.log("Nuevo estado de diálogos:", newDialogs);
      return newDialogs;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <Card className="border-orange-200">
        <TableHeader
          onJoinTables={handleJoinTables}
          disabled={
            !Array.isArray(tables) ||
            tables.length === 0 ||
            selectedTables.size < 2
          }
        />
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <TableList
            tables={tables}
            selectedTables={selectedTables}
            onToggleSelect={handleToggleSelect}
            onDelete={handleDelete}
            onSeparate={handleSeparate}
          />
        </CardContent>
      </Card>

      <ConfirmationDialog
        isOpen={dialogs.delete.isOpen}
        onClose={() =>
          setDialogs((prev) => ({
            ...prev,
            delete: { isOpen: false, tableId: null },
          }))
        }
        onConfirm={handleDeleteConfirm}
        title="Eliminar Mesa"
        description="¿Está seguro que desea eliminar esta mesa?"
      />

      <ConfirmationDialog
        isOpen={dialogs.join.isOpen}
        onClose={() =>
          setDialogs((prev) => ({ ...prev, join: { isOpen: false } }))
        }
        onConfirm={handleJoinTablesConfirm}
        title="Unir Mesas"
        description="¿Está seguro que desea unir las mesas seleccionadas?"
      />

      <ConfirmationDialog
        isOpen={dialogs.separate.isOpen}
        onClose={() =>
          setDialogs((prev) => ({
            ...prev,
            separate: { isOpen: false, tableId: null },
          }))
        }
        onConfirm={handleSeparateConfirm}
        title="Separar Mesas"
        description="¿Está seguro que desea separar las mesas unidas?"
      />
    </div>
  );
};

export default ShowTables;
